export type BuyerInquiryState =
  | 'idle'
  | 'pending'
  | 'success'
  | 'validation-failure'
  | 'transient-failure'

export type BuyerInquiryFailure = {
  state: 'validation-failure' | 'transient-failure'
  message: string
  traceId?: string
}

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : undefined

const responseMessage = (data: unknown) => {
  if (typeof data === 'string' && data.trim()) return data

  const payload = asRecord(data)
  if (!payload) return undefined

  for (const property of ['Message', 'ExceptionMessage', 'message']) {
    const value = payload[property]
    if (typeof value === 'string' && value.trim()) return value
  }

  return undefined
}

const safeTraceId = (value: unknown) => {
  if (typeof value !== 'string') return undefined
  const traceId = value.trim()
  return /^[a-zA-Z0-9._-]{1,128}$/.test(traceId) ? traceId : undefined
}

const responseTraceId = (response: Record<string, unknown> | undefined) => {
  const headers = asRecord(response?.headers)
  const data = asRecord(response?.data)
  return safeTraceId(headers?.['x-correlation-id']) || safeTraceId(data?.traceId)
}

const withTrace = (failure: BuyerInquiryFailure, fallbackTraceId?: string): BuyerInquiryFailure => {
  const traceId = failure.traceId || safeTraceId(fallbackTraceId)
  if (traceId) return {...failure, traceId}

  const withoutEmptyTrace = {...failure}
  delete withoutEmptyTrace.traceId
  return withoutEmptyTrace
}

export const classifyBuyerInquiryError = (
  error: unknown,
  fallbackTraceId?: string
): BuyerInquiryFailure => {
  const failure = asRecord(error)
  const response = asRecord(failure?.response)
  const status = typeof response?.status === 'number' ? response.status : undefined
  const serverMessage = responseMessage(response?.data)
  const traceId = responseTraceId(response)

  if (status === 400 || status === 422) {
    return withTrace(
      {
        state: 'validation-failure',
        message: serverMessage || 'شناسه ملی یا شماره اقتصادی واردشده معتبر نیست.',
        traceId,
      },
      fallbackTraceId
    )
  }
  if (status === 401) {
    return withTrace(
      {
        state: 'transient-failure',
        message: 'احراز هویت برای استعلام انجام نشد. لطفاً دوباره وارد سامانه شوید.',
        traceId,
      },
      fallbackTraceId
    )
  }
  if (status === 403) {
    return withTrace(
      {
        state: 'transient-failure',
        message: 'دسترسی لازم برای استعلام شماره اقتصادی وجود ندارد.',
        traceId,
      },
      fallbackTraceId
    )
  }
  if (status === 408 || status === 504 || failure?.code === 'ECONNABORTED') {
    return withTrace(
      {
        state: 'transient-failure',
        message:
          serverMessage ||
          'استعلام در مهلت مقرر پاسخ نداد. کد واردشده لزوماً نامعتبر نیست؛ لطفاً کمی بعد دوباره تلاش کنید.',
        traceId,
      },
      fallbackTraceId
    )
  }
  if (status === 429) {
    return withTrace(
      {
        state: 'transient-failure',
        message:
          serverMessage ||
          'تعداد درخواست‌های استعلام بیش از حد مجاز است. لطفاً کمی بعد دوباره تلاش کنید.',
        traceId,
      },
      fallbackTraceId
    )
  }
  if (status !== undefined && status >= 400 && status < 500 && serverMessage) {
    return withTrace({state: 'transient-failure', message: serverMessage, traceId}, fallbackTraceId)
  }

  return withTrace(
    {
      state: 'transient-failure',
      message:
        'امکان استعلام شماره اقتصادی وجود ندارد. کد واردشده لزوماً نامعتبر نیست؛ لطفاً کمی بعد دوباره تلاش کنید.',
      traceId,
    },
    fallbackTraceId
  )
}

export const isBuyerPreInquirySatisfied = (
  requiresInquiry: boolean,
  hasSuccessfulInquiry: boolean,
  inquiryState: BuyerInquiryState
) => !requiresInquiry || hasSuccessfulInquiry || inquiryState === 'transient-failure'
